<template>
  <div class="app">
    <!-- Embedded, the host owns the sidebar and renders <titan-remote-nav>
         in its own frame; rendering ours too would show two sidebars. -->
    <cl-sidebar v-if="!embedded" v-model="isClosed">
      <template #company-logo="{ isClosed }">
        <div
          v-if="isRequestPending"
          class="tw-w-[154px] tw-h-[52px] tw-flex tw-justify-center tw-items-center"
        >
          <cl-spinner
            :loading-text="$t('loading')"
            :size="'small'"
            class="tw-mr-2"
          />
        </div>
        <t-logo
          v-else
          @click.prevent="redirectBasedOnRole"
          :logo-src="logoSrc"
          v-show="!isClosed"
        />
      </template>
      <template #dropdown-select="{ isClosed }">
        <t-nav-dropdown
          v-if="
            selectedAccount?.type_name === 'System' ||
            selectedAccount?.type_name === 'MSP' ||
            selectedAccount?.type_name === 'Distributor'
          "
          icon="cb-icon-customers"
          data-test-id="navbar-select-customer"
          :loading="false"
          :loading-more="loadingMore"
          :is-closed="isClosed"
          :options="userAccountList || []"
          :initial-selected="initialMsp?.uuid"
          data-clarity-mask="true"
          @reset-list="setDropDownList"
          @search="itemBeingSearched"
          @selected="customerSelected"
          @load-more="loadMore"
        />
      </template>
      <template #side-nav="{ isClosed: isSidebarCollapsed }">
        <t-side-nav
          v-if="!loadingSolutions"
          :nav-menu-set-up="customerNavMenuSetUp"
          :is-sidebar-collapsed="isSidebarCollapsed"
          :tier="tier"
        ></t-side-nav>
      </template>
      <template #info-section="{ isClosed }" v-if="isDefaultBranding">
        <img
          v-if="!isClosed"
          alt="CyberSentriq logo"
          :src="cyberSentriqLogoSrc"
        />
      </template>
    </cl-sidebar>

    <!-- Embedded: the host owns the sidebar frame, so the SAME nav this
         layout already computes is teleported into its outlet instead.
         Rebuilding it from the shared nav state would drop this layout's
         licence/solution filtering and show unlicensed products. -->
    <titan-remote-nav-outlet v-if="embedded">
      <!-- Expanded, explicitly: `isSidebarCollapsed` above is cl-sidebar's
           #side-nav slot prop, which does not exist out here — embedded, that
           slot is never rendered. The host owns its sidebar frame and exposes no
           collapse state to the remote, so there is nothing to track yet. -->
      <t-side-nav
        v-if="!loadingSolutions"
        :nav-menu-set-up="customerNavMenuSetUp"
        :is-sidebar-collapsed="false"
        :tier="tier"
      ></t-side-nav>
    </titan-remote-nav-outlet>
    <div class="app-content">
      <!-- Embedded, the host owns the app header. -->
      <t-upper-nav v-if="!embedded" />
      <main
        class="tw-flex-1 tw-overflow-y-auto tw-bg-primary-grey-25 tw-p-4"
        :class="{ 'max-lg:tw-ml-14': !embedded }"
      >
        <div class="tw-flex tw-flex-col tw-gap-2">
          <read-only-role-banner
            :visible="isReadOnlyRoleVisible"
            level="customer"
          />
          <sfi-migration-banner :visible="isSfiMigrationBannerVisible" />

          <ms-banner v-if="isBanner && !getAzureAccounts.length"></ms-banner>
        </div>

        <div class="tw-w-full">
          <t-page-header
            :product-links="[]"
            :user-links="userLinks"
            :data-test-id="'header-user-menu'"
          />
          <router-view v-if="!loadingSolutions" />
          <div
            v-else
            class="tw-flex tw-justify-center tw-items-center tw-h-full"
          >
            <cl-spinner :loading-text="$t('loading')" />
          </div>
        </div>
      </main>
      <cl-footer
        v-if="!embedded"
        :other-products-text="$t('other-products')"
        :try-now-text="$t('try-now')"
        :msp-name="getOrganisationBranding.name ?? undefined"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmbedded } from "@/remote/contract";
import {
  ref,
  onBeforeMount,
  computed,
  onMounted,
  watch,
  type Ref,
  type ComputedRef,
} from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useRouter, useRoute } from "vue-router";
import { getMediaUrl } from "@/config/runtimeConfig";

import { TIER } from "@/constants";

import msBanner from "@/views/Common/MicrosoftBanner/index.vue";
import SfiMigrationBanner from "@/views/Common/SfiMigrationBanner.vue";
import TPageHeader from "@/components/TPageHeader";
import TNavDropdown from "@/components/TNavDropdown";
import {
  usePrimaryNavItems,
  useEnabledSolutions,
  useRedirectBasedOnRole,
} from "@/composables";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import { useFlag } from "@/composables/useFlags/useFlag";

import { NAV_ITEMS_MODE } from "@/constants";
import TSideNav from "@/components/TSideNav";

import { ALL_SERVICES, type AllServicesObject } from "@/utils/services";
import TLogo from "@/components/TLogo/TLogo.vue";
import TUpperNav from "@/components/TUpperNav/TUpperNav.vue";
import ReadOnlyRoleBanner from "@/views/Common/ReadOnlyRoleBanner.vue";
import { isNavItemDisabled } from "@/utils/navItemHelpers";
import {
  useRequestStatus,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import type { UserLink } from "@/components/TPageHeader/TPageHeader.types";
import type { ChildItem, DataItem } from "@/components/TSideNav/types";
import { getAccountShortName } from "@/utils/getAccountShortName";
import { DEFAULT_BRANDING_NAME } from "@/modules/Settings/constants";
import { storeToRefs } from "pinia";
import { useSidebarStore } from "@/store/sidebar";
import { useSolutionsStore } from "@/store/solutions.store";
import { useDropDownStore } from "@/store/dropDown.store";
import { useAzureStore } from "@/store/azure.store";
import { ADMIN, SUPER_ADMIN } from "@/authorisation/authorisation.types";
import { useBrandingStore } from "@/store/branding.store";
import { usePageMetaStore } from "@/store/pageMeta.store";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";
import { navigateToCustomerOrganisation } from "@/utils/navigation/organisationNavigation";
import TitanRemoteNavOutlet from "@/remote/TitanRemoteNavOutlet.vue";

// Snapshotted once at boot by the contract module.
const embedded = isEmbedded();
import type {
  AuthUser,
  SelectedAccount,
  SelectedMsp,
  SelectedCustomer,
  EnabledSolution,
  DropDownAccount,
  DropdownItem,
  UserAccount,
} from "./CustomerLayout.types";

const authStore = useAuthenticationStore();
const router = useRouter();
const route = useRoute();
const redirectBasedOnRole = useRedirectBasedOnRole();

const { enabledSolutions } = useEnabledSolutions();
const { customerGlobalOrganisationId, mspGlobalOrganisationId } =
  useGlobalOrganisationID();
const { getNavItems } = usePrimaryNavItems();
const { isFeatureAvailable: isConnectionsV2Available } =
  useFlag("CONNECTIONS_V2");
const { setRequestStatus, isRequestPending } = useRequestStatus();

// Reactive, flag-filtered nav items: gated entries (e.g. geoblocking,
// quarantine config) appear once the Harness SDK resolves their flag. Reading
// the registry once at setup froze them to the static matrix fallback.
const customerAdminNavItems = getNavItems(NAV_ITEMS_MODE.CUSTOMER_ADMIN);
const loadingSolutions: Ref<boolean> = ref(true);
const isBanner: Ref<boolean> = ref(false);
const initialMsp: Ref<SelectedMsp | null> = ref(null);
const tier = TIER.customer;
const userAccountList: Ref<UserAccount[] | null> = ref(null);
const userLinks: Ref<UserLink[]> = ref([]);
const loadingMore = ref(false);
const currentPage = ref(1);
const currentFilter = ref("");
const sidebarStore = useSidebarStore();
const { isClosed } = storeToRefs(sidebarStore);
const solutionsStore = useSolutionsStore();
const dropDownStore = useDropDownStore();
const azureStore = useAzureStore();
const brandingStore = useBrandingStore();
const pageMetaStore = usePageMetaStore();
const { organisationBranding: getOrganisationBranding } =
  storeToRefs(brandingStore);

const getSolutions = async (uuid: string): Promise<void> => {
  await solutionsStore.getSolutions({
    uuid,
  });
};

const selectedMsp: ComputedRef<SelectedMsp | null> = computed(
  () => authStore.selectedMsp as SelectedMsp | null,
);

const selectedAccount: ComputedRef<SelectedAccount | null> = computed(
  () => authStore.selectedAccount as SelectedAccount | null,
);

const authUser: ComputedRef<AuthUser | null> = computed(
  () => authStore.authUser as AuthUser | null,
);

const selectedCustomer: ComputedRef<SelectedCustomer | null> = computed(
  () => authStore.selectedCustomer as SelectedCustomer | null,
);

const { azureAccounts: getAzureAccounts, isLegacyApp } =
  storeToRefs(azureStore);

const dropDownList: ComputedRef<DropDownAccount[]> = computed(
  () => dropDownStore.list,
);
const dropDownTotal = computed<number>(() => dropDownStore.total);
const hasMore = computed(() => dropDownList.value.length < dropDownTotal.value);

const isReadOnlyRoleVisible: ComputedRef<boolean> = computed(() => {
  return authUser.value?.role === "admin-ro";
});

const { isFeatureAvailable: isAppMigrationEnabled } = useFlag(
  "M365_AZURE_APP_MIGRATION",
);

const isSfiMigrationBannerVisible: ComputedRef<boolean> = computed(() => {
  const isAdmin =
    authUser.value?.role === ADMIN || authUser.value?.role === SUPER_ADMIN;
  return isAppMigrationEnabled.value && isAdmin && isLegacyApp.value;
});

const customerNavMenuSetUp: ComputedRef<Record<string, DataItem[]>> = computed(
  () => {
    const items = customerAdminNavItems.value
      .map((item: DataItem) => {
        const isSolutionDisabled = ref(false);

        if (item.solutionId) {
          isSolutionDisabled.value = !enabledSolutions.value.some(
            (solution: EnabledSolution) =>
              solution.service.uuid === item.solutionId,
          );
        }

        // If no license then solution should not be visible on Customer level
        // https://gitlab.titanhq.com/titanhq/dev/scrum/team-platfarmers/-/issues/1624
        if (isSolutionDisabled.value) {
          return;
        }

        const excludedConnectionsTestId = isConnectionsV2Available.value
          ? "customer-settings-connections"
          : "customer-settings-connections-v2";

        return {
          ...item,
          disabled: isSolutionDisabled.value,
          children: item.children
            ?.filter(
              (child: ChildItem) =>
                child.dataTestId !== excludedConnectionsTestId &&
                !isNavItemDisabled({
                  navItem: child,
                  enabledSolutions: enabledSolutions.value,
                  parentDisabled: isSolutionDisabled.value,
                  mspGlobalOrganisationId: mspGlobalOrganisationId.value,
                }),
            )
            .map((child: ChildItem) => {
              return {
                ...child,
                children: child.children?.filter(
                  (grandChild: ChildItem) =>
                    !isNavItemDisabled({
                      navItem: grandChild,
                      enabledSolutions: enabledSolutions.value,
                      parentDisabled: false,
                      mspGlobalOrganisationId: mspGlobalOrganisationId.value,
                    }),
                ),
              };
            }),
        };
      })
      .filter(Boolean) as unknown as DataItem[];

    return separatedByGroup(items);
  },
);

const logoSrc: ComputedRef<string> = computed(() => {
  if (getOrganisationBranding.value.logo_dark) {
    return `${getMediaUrl()}/${getOrganisationBranding.value.logo_dark}`;
  }
  return new URL("@/assets/branding/Titan_Logo.svg", import.meta.url).href;
});

const isDefaultBranding = computed<boolean>(() => {
  return !!(
    !getOrganisationBranding.value.logo_dark &&
    getOrganisationBranding.value.name === DEFAULT_BRANDING_NAME
  );
});

const cyberSentriqLogoSrc = new URL(
  "@/assets/branding/powered-by-cybersentriq.svg",
  import.meta.url,
).href;

onMounted(async () => {
  setRequestStatus({ type: PENDING });
  await brandingStore.getBranding({
    organisationUuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
  });
  setRequestStatus({ type: READY });
});

const redirectToActiveSolution = async () => {
  // Don't redirect if solutions are still loading or empty
  if (loadingSolutions.value || !enabledSolutions.value.length) {
    return;
  }

  let isSolutionDisabled = false;

  isSolutionDisabled = !enabledSolutions.value.some(
    (solution: EnabledSolution) =>
      solution.solution_uuid === route.meta.solutionId,
  );

  if (isSolutionDisabled) {
    // Find next default route name for customer based on the next available solution
    // TODO - remove this when customer dashboard is available and replace "customerUsageRoute" to dashboard one!
    // Iterate ALL_SERVICES in priority order and pick the first one the customer
    // has enabled. Solutions on the licence but missing from ALL_SERVICES (e.g.
    // SEG, which is gated per-route inside email_security) are not landing-eligible.
    const findService = ALL_SERVICES.find((service: AllServicesObject) =>
      enabledSolutions.value.some(
        (solution: EnabledSolution) => solution.solution_uuid === service.uuid,
      ),
    );
    const nextRouteName = findService
      ? findService.customerDefaultRouteName
      : "customerUsageRoute";

    await router.push({ name: nextRouteName });
  }
};

onBeforeMount(async () => {
  if (!customerGlobalOrganisationId.value) {
    return;
  }

  try {
    loadingSolutions.value = true;

    solutionsStore.resetState();
    await getSolutions(customerGlobalOrganisationId.value);
    await brandingStore.getBranding({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
    });
  } finally {
    if (route.meta.solutionId) {
      redirectToActiveSolution();
    }

    loadingSolutions.value = false;
    // Call redirect again after solutions are loaded, in case it was skipped earlier
    if (route.meta.solutionId) {
      redirectToActiveSolution();
    }
  }
});

const initializeLayout = async (): Promise<void> => {
  let parentPath = "";
  if (selectedAccount.value?.type_name === "System") {
    parentPath += "/system/msp";
  }
  if (selectedAccount.value?.type_name === "MSP") {
    parentPath += "/msp";
  }
  if (selectedAccount.value?.type_name === "Distributor") {
    parentPath += "/msp";
  }

  pageMetaStore.setParentPath(parentPath);

  if (dropDownList.value) {
    userAccountList.value = cleanAccounts(dropDownList.value);
    if (userAccountList.value[0] && selectedCustomer.value?.name) {
      userAccountList.value[0].name = selectedCustomer.value.name;
    }
  }

  if (selectedCustomer.value?.name) {
    pageMetaStore.setCustomerName(selectedCustomer.value.name);
  }
};

const initializeData = async (): Promise<void> => {
  setSupportLinks();
  try {
    const organisationUuid =
      selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "";

    // These two fetches are independent; use allSettled so a failing
    // connection-status request does not skip the numberOfSeats call below and
    // leave azureSeats null for the whole session. The connection-status fetch
    // only feeds the legacy-app detection (SFI banner), so it is gated behind
    // M365_AZURE_APP_MIGRATION — with the flag off the layout fetches exactly
    // what it did before P-287.
    await Promise.allSettled([
      azureStore.getAllAzureAccounts(organisationUuid),
      ...(isAppMigrationEnabled.value
        ? [azureStore.getConnectionStatus(organisationUuid)]
        : []),
    ]);

    if (getAzureAccounts.value.length > 0) {
      await azureStore.numberOfSeats({
        organisation:
          selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "",
        azureID: getAzureAccounts.value[0].id,
      });
    }
  } catch (_e) {
    // swallow errors; handled upstream
  } finally {
    isBanner.value = !getAzureAccounts.value.length;
  }
};

onMounted(async () => {
  await initializeLayout();
  await initializeData();
});

const customerSelected = async (customer: DropdownItem): Promise<void> => {
  const customerData = customer as UserAccount;
  if (customerData.allCustomers) {
    await authStore.setCustomer(null);
    await router.push(
      buildOrgUrl("msp", selectedMsp.value?.uuid || null, "customers"),
    );
  } else {
    await azureStore.clearAzureCache();
    await navigateToCustomerOrganisation(
      { router },
      { id: customerData.uuid || "", name: customerData.name },
    );
    // Embedded, `router.go(0)` reloads the host's whole document — we share its history.
    if (!embedded) {
      router.go(0);
    }
  }
};

const separatedByGroup = (
  customerArray: DataItem[],
): Record<string, DataItem[]> => {
  const customerByGroup: Record<string, DataItem[]> = {};

  customerArray.forEach((item: DataItem) => {
    if (customerByGroup[item.groupBy]) {
      customerByGroup[item.groupBy].push(item);
    } else {
      customerByGroup[item.groupBy] = [item];
    }
  });
  return customerByGroup;
};

const setSupportLinks = (): void => {
  const id = authUser.value?.uuid;

  userLinks.value = [
    {
      title: "Support", // Using string literal since $t is not available in setup
      link: "https://support.titanhq.com/en/69450-welcome-to-the-titanhq-platform-.html",
      target: "_blank",
      icon: "cb-icon-support",
    },
    {
      title: "Security",
      link: `/profile/security?id=${id}`,
      target: "_blank",
      icon: "cb-icon-settings",
    },
  ];
};

const getParentUuid = () =>
  selectedMsp.value?.uuid || selectedAccount.value?.uuid;

const setDropDownList = async (): Promise<void> => {
  currentPage.value = 1;
  currentFilter.value = "";
  try {
    dropDownStore.resetState();
    await dropDownStore.getDropDownList({
      globalOrganisationID: getParentUuid(),
      params: {
        filter: "",
        results: 40,
        page: 1,
        sortkey: "name",
        sortdir: "asc",
      },
    });
  } finally {
    if (dropDownList.value) {
      userAccountList.value = cleanAccounts(dropDownList.value);
    }
  }
};

const itemBeingSearched = async (item: string): Promise<void> => {
  currentPage.value = 1;
  currentFilter.value = item;
  try {
    dropDownStore.resetState();
    await dropDownStore.getDropDownList({
      globalOrganisationID: getParentUuid(),
      params: {
        filter: item,
        results: 40,
        page: 1,
        sortkey: "name",
        sortdir: "asc",
      },
    });
  } finally {
    // handled by watcher
  }
};

const loadMore = async (): Promise<void> => {
  if (loadingMore.value || !hasMore.value) return;
  loadingMore.value = true;
  currentPage.value++;
  try {
    await dropDownStore.appendDropDownList({
      globalOrganisationID: getParentUuid(),
      params: {
        filter: currentFilter.value,
        results: 40,
        page: currentPage.value,
        sortkey: "name",
        sortdir: "asc",
      },
    });
  } finally {
    loadingMore.value = false;
  }
};

const cleanAccounts = (accounts: DropDownAccount[]): UserAccount[] => {
  const allAccounts = accounts
    .map((acct: DropDownAccount) => {
      return {
        ...acct,
        id: acct.uuid,
        name: acct.name || "",
        short: getAccountShortName(acct.name || ""),
        active: false,
        allCustomers: false,
        placeholder: "Search Customers",
      };
    })
    .filter((res: DropDownAccount) => res.organisation_type === "Customer");

  const otherAccounts: UserAccount[] = [
    {
      id: "all-customers",
      uuid: "all-customers",
      name: "All Customers",
      short: "AM",
      allMsps: false,
      allCustomers: true,
      active: false,
      placeholder: "Search Customers",
    },
  ];

  return [...otherAccounts, ...allAccounts];
};

watch(
  dropDownList,
  () => {
    userAccountList.value = cleanAccounts(dropDownList.value);
  },
  { deep: true, immediate: true },
);
</script>
